import {
    EzObject,
    EzString,
    EzArray
} from '/ezlibrary/helpers/EzHelpers.js';
import { EzRegistrationState } from '/ezlibrary/enums/EzRegistrationState.js';
import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEnumeration2 } from '/ezlibrary/enums/EzEnumeration2.js';
import { EzClockerRole } from '/ezlibrary/EzClockerRole.js';


/**
 * TODO: Migrate all globalThis.ezApi.ezclocker.ezUserRole or globalThis.ezApi.ezclocker.enums.ezUserRole references
 * to use static method/properties from EzUserRole
 * TODO: Remove legacy registration once all globalThis.ezApi.ezclocker.ezUserRole or globalThis.ezApi.ezclocker.enums.ezUserRole references get migrated
 * to use static method/properties from EzUserRole
 * @class
 * @extends {EzEnum}
 * @description
 * Creates a new instance of the EzUserRole enumeration.
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Available enumerations:
 *      EzUserRole.UNKNOWN = 'UNKNOWN'
 *      EzUserRole.NONE = 'NONE'
 *      EzUserRole.ROLE_ANONYMOUS = 'ROLE_ANONYMOUS'
 *      EzUserRole.ROLE_ADMIN = 'ROLE_ADMIN'
 *      EzUserRole.ROLE_EZCLOCKER_SUPPORT = 'ROLE_EZCLOCKER_SUPPORT'
 *      EzUserRole.ROLE_ENGINEERING_DEBUG = 'ROLE_ENGINEERING_DEBUG'
 *      EzUserRole.ROLE_DEVELOPER = 'ROLE_DEVELOPER'
 *      EzUserRole.ROLE_EMPLOYER = 'ROLE_EMPLOYER'
 *      EzUserRole.ROLE_PAYROLL_MANAGER = 'ROLE_PAYROLL_MANAGER'
 *      EzUserRole.ROLE_MANAGER = 'ROLE_MANAGER'
 *      EzUserRole.ROLE_EMPLOYEE = 'ROLE_EMPLOYEE'
 *      EzUserRole.ROLE_PERSONAL = 'ROLE_PERSONAL'
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Available static functions inherited from EzEnumeration2 parent:
 *      1) EzUserRole.ezToDisplayName(enumPropertyValue): String
 *      2) EzUserRole.ezAsEnum(enumPropertyNameOrValue)
 *      3) EzUserRole.ezAsEnumOrDefault(enumPropertyNameOrValue, defaultEnumPropertyValue)
 *      4) EzUserRole.ezValueOf(enumPropertyName)
 *      5) EzUserRole.ezValueOfOrDefault(enumPropertyName, defaultEnumPropertyValue)
 *      6) EzUserRole.ezNameOf(enumPropertyValue)
 *      7) EzUserRole.ezNameOfOrDefault(enumPropertyValue, defaultEnumPropertyValue)
 *      8) EzUserRole.ezEnumData(enumPropertyValue)
 *      9) EzUserRole.ezEnumDataOrDefault(enumPropertyValue, defaultEnumData)
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Available static functions from EzUserRole implementation:
 *      10) EzUserRole.ezToRoleName(enumValue): String
 *      11) EzUserRole.ezAuthorityNameToRoleName(enumValue): String [DEPRECATED - Migrate to EzUserRole.ezToRoleName()]
 *      12) EzUserRole.ezToLegacyAccountType(enumValue): String
 *      13) EzUserRole.ezAuthorityNameToAccountType(enumValue): String [DEPRECATED - Migrate to EzUserRole.ezToLegacyAccountType()]
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other non-enumeration classes with:
 *     import {
 *         // ... other enumerations ...
 *         EzUserRole
 *     } from '/ezlibrary/enums/EzEnumerations.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import into other enumeration classes with:
 *     import { EzUserRole } from '/ezlibrary/EzUserRole.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * @deprecated
 * Migreate to using enumeration class EzAccountRole (src/main/webapp/ezlibrary/enums/EzAccountRole.js)
 *  Import EzAccountRole into non-enumeration classes with:
 *      import {
 *          // ... other enumeration classes ...
 *          EzAccountRole
 *      } from '/ezlibrary/enums/EzEnumerations.js';
 *
 *  Import EzAccountRole into other enumeration classes or classes that cannot import EzEnumerations:
 *      import { EzAccountRole } from '/ezlibrary/enums/EzAccountRole.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzUserRole extends EzEnumeration2 {
    /**
     * @static
     * @public @method
     * Example static method that returns the enumeration property's data.display value.
     * If the enumPropertyValue is not a valid enumeration property value, then EzUserRole.UNKNOWN.display is returned.
     * @param {undefined|null|string} enumPropertyValue
     * A valid enumeration property value from EzUserRole
     * @returns {string}
     */
    static ezToDisplayName(enumPropertyValue) {
        return EzString.stringOrEmpty(
            EzString.hasLength(enumPropertyValue)
                ? EzUserRole.ezEnumData(enumPropertyValue)?.display
                : EzUserRole.ezEnumData(EzUserRole.UNKNOWN)?.display);
    }

    /**
     * @static
     * @private @field
     * Cached Set of valid role values for whitelist validation.
     * Safe to populate before ezApi is ready (no EzClockerRole construction).
     * @type {Set<string>|null}
     */
    static #ezValidRoleValuesCache = null;
    /**
     * @static
     * @private @readonly @property
     * Set of valid EzUserRole enumeration values for whitelist validation.
     * @returns {Set<string>}
     */
    static get #ezValidRoleValues() {
        if (!EzObject.isValid(this.#ezValidRoleValuesCache)) {
            this.#ezValidRoleValuesCache = new Set([
                EzUserRole.UNKNOWN,
                EzUserRole.NONE,
                EzUserRole.ROLE_ANONYMOUS,
                EzUserRole.ROLE_ADMIN,
                EzUserRole.ROLE_EZCLOCKER_SUPPORT,
                EzUserRole.ROLE_DEVELOPER,
                EzUserRole.ROLE_EMPLOYER,
                EzUserRole.ROLE_PAYROLL_MANAGER,
                EzUserRole.ROLE_MANAGER,
                EzUserRole.ROLE_EMPLOYEE,
                EzUserRole.ROLE_PERSONAL
            ]);
        }

        return this.#ezValidRoleValuesCache;
    }

    /**
     * @static
     * @public @method
     * Gets the RoleName for the provided enumValue
     * @param {String} enumValue
     * @returns {String}
     */
    static ezToRoleName(enumValue) {
        if (EzString.hasText(enumValue) && EzUserRole.#ezValidRoleValues.has(enumValue)) {
            const extPropName = `_${enumValue}`;

            return EzObject.isValid(EzUserRole?.[extPropName])
                ? EzUserRole[extPropName].roleName
                : EzUserRole._UNKNOWN.roleName;
        }

        return EzObject.assignOrDefault(
            EzUserRole.ezEnumData(EzUserRole.UNKNOWN)?.roleName,
            EzUserRole.UNKNOWN);
    }

    /**
     * @static
     * @public @function
     * Gets the role name for the provided authority role.
     * @param {String} enumValue
     * @returns {String}
     * @deprecated
     * Migrate to: EzUserRole.ezToRoleName();
     */
    static ezAuthorityNameToRoleName(enumValue) {
        return EzUserRole.ezToRoleName(enumValue);
    }

    /**
     * @static
     * @public @function
     * Gets the legacy AccountType value for the provided enumValue
     * @param {string} enumValue
     * @returns {string}
     */
    static ezToLegacyAccountType(enumValue) {
        if (EzString.hasText(enumValue) && EzUserRole.#ezValidRoleValues.has(enumValue)) {
            const extPropName = `_${enumValue}`;

            return EzObject.isValid(EzUserRole?.[extPropName])
                ? EzUserRole[extPropName].legacyAccountType
                : EzObject.assignOrDefault(
                    EzUserRole.ezEnumData(EzUserRole.UNKNOWN)?.legacyAccountType,
                    EzUserRole.UNKNOWN);
        }

        return EzObject.assignOrDefault(
            EzUserRole?._UNKNOWN?.legacyAccountType,
            EzUserRole.UNKNOWN);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided enumValue to a legacy account type.
     * @returns {string}
     * A legacy account type for the provided enum value.
     * @deprecated
     * Migrate to EzUserRole.ezToLegacyAccountType()
     */
    static ezAuthorityNameToAccountType(enumValue) {
        return EzUserRole.ezToLegacyAccountType(enumValue);
    }

    /**
     * @static
     * @public @function
     * Transforms the provided EzAccountRole enum property value to its equivlant EzUserRole enum property value.
     * If the provided ezAccountRoleEnumValue is not a valid EzAccountRole OR does not have a equivlant EzUserRolle enum property value,
     * then EzUserRole.ROLE_ANONYMOUS is returned.
     * @param {string} [ezAccountRoleEnumValue= 'ROLE_ANONYMOUS']
     * A valid EzAccountRole enum property value.
     * @returns {string}
     * A valid EzUserRole enum property value
     */
    static fromEzAccountRole(ezAccountRoleEnumValue = 'ROLE_ANONYMOUS') {
        if (!EzString.hasText(ezAccountRoleEnumValue)) {
            return EzUserRole.ROLE_ANONYMOUS;
        }

        switch (ezAccountRoleEnumValue) {
            case EzUserRole.ROLE_ADMIN:
                return EzAccountRole.ROLE_ADMIN;
            case EzUserRole.ROLE_EZCLOCKER_SUPPORT:
                return EzAccountRole.ROLE_EZCLOCKER_SUPPORT;
            case EzUserRole.ROLE_ENGINEERING_DEBUG:
                return EzAccountRole.ROLE_ENGINEERING_DEBUG;
            case EzUserRole.ROLE_DEVELOPER:
                return EzAccountRole.ROLE_DEVELOPER;
            case EzUserRole.ROLE_EMPLOYER:
                return EzAccountRole.ROLE_EMPLOYER;
            case EzUserRole.ROLE_PAYROLL_MANAGER:
                return EzAccountRole.ROLE_PAYROLL_MANAGER;
            case EzUserRole.ROLE_MANAGER:
                return EzAccountRole.ROLE_MANAGER;
            case EzUserRole.ROLE_EMPLOYEE:
                return EzAccountRole.ROLE_EMPLOYEE;
            case EzUserRole.ROLE_PERSONAL:
                return EzAccountRole.ROLE_PERSONAL;
            case EzUserRole.ROLE_ANONYMOUS:
            default:
                return EzUserRole.ROLE_ANONYMOUS;
        }
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Initialization Section: ONLY PLACE INITIALIZATION CODE BEYOND THIS SECTION
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @private @field
     * Stores the singleton instance of this enumeration class.
     * @type {EzUserRole}
     * Internal static instance of the EzUserRole
     */
    static #ezUserRoleSingleton = null;

    /**
     * @static
     * @private @method
     * Builds the enumeration properties and values for EzClockerAccountType
     */
    static #ezBuildEnumeration() {
        let ezClockerRole = new EzClockerRole(
            // Authority Name
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            // EzClockerAccountType
            'ANONYMOUS',
            'Unknown',
            // Context Name
            'unknownContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'No access to Employer Dashboard website page.',
                'No access to Employer Schedules website page.',
                'No access to Employer Archive website page.',
                'No access to Employer Account website page.',
                'No access to the Employee Dashboard website page.',
                'No access to the Employee Schedule website page.',
                'No access to the Personal Dashboard website page',
                'No access to the Developer Account website page.',
                'No access to the Administration Portal website page.',
                'No access to the Employer mobile app.',
                'No access to the Employee mobile app.',
                'No access to the Personal mobile app.'
            ],
            // Internal Only
            false,
            // Legacy Account Type
            'unknown',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY);

        this.#ezUserRoleSingleton.ezBuildEnumProp(
            // Enumeration Property Name
            'UNKNOWN',
            // Enumeration Property Value
            'UNKNOWN',
            // Optional Enumeration Data Value
            ezClockerRole);

        this._ROLE_UNKNOWN = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            // EzClockerAccountType
            'ANONYMOUS',
            'None',
            // Context Name
            'noneContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'No access to Employer Dashboard website page.',
                'No access to Employer Schedules website page.',
                'No access to Employer Archive website page.',
                'No access to Employer Account website page.',
                'No access to the Employee Dashboard website page.',
                'No access to the Employee Schedule website page.',
                'No access to the Personal Dashboard website page',
                'No access to the Developer Account website page.',
                'No access to the Administration Portal website page.',
                'No access to the Employer mobile app.',
                'No access to the Employee mobile app.',
                'No access to the Personal mobile app.'
            ],
            // Internal Only
            false,
            // Legacy Account Type
            'none',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY);

        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'NONE',
            'NONE',
            ezClockerRole);

        this._ROLE_NONE = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            // Authority name
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            // EzClockerAccountType
            'ANONYMOUS',
            // Role name
            'Anonymous',
            // Context Name
            'anonymousContext',
            // Access Information
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'No access to Employer Dashboard website page.',
                'No access to Employer Schedules website page.',
                'No access to Employer Archive website page.',
                'No access to Employer Account website page.',
                'No access to the Employee Dashboard website page.',
                'No access to the Employee Schedule website page.',
                'No access to the Personal Dashboard website page',
                'No access to the Developer Account website page.',
                'No access to the Administration Portal website page.',
                'No access to the Employer mobile app.',
                'No access to the Employee mobile app.',
                'No access to the Personal mobile app.'
            ],
            // Internal only
            false,
            // Account type
            'anonymous',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)

        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_ANONYMOUS',
            'ROLE_ANONYMOUS',
            ezClockerRole);

        this._ROLE_ANONYMOUS = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            // Authority name
            'ROLE_ADMIN',
            'ROLE_ADMIN',
            'ROLE_ADMIN',
            // EzClockerAccountType
            'ADMIN',
            // Role name
            'Administrator',
            // Context Name
            'adminContext',
            // Access Information
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'Full access to Employer Dashboard website page.',
                'Full access to Employer Schedules website page.',
                'Full access to Employer Archive website page.',
                'Full access to Employer Account website page.',
                'Full access to the Employee Dashboard website page.',
                'Full access to the Employee Schedule website page.',
                'Full access to the Personal Dashboard website page',
                'Full access to the Developer Account website page.',
                'Full access to the Administration Portal website page.',
                'Full access to the Employer mobile app.',
                'Full access to the Employee mobile app.',
                'Full access to the Personal mobile app.'
            ],
            // Internal Only
            true,
            // Legacy Account Type
            'administrator',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_ADMIN',
            'ROLE_ADMIN',
            ezClockerRole);

        this._ROLE_ADMIN = ezClockerRole;


        ezClockerRole = new EzClockerRole(
            'ROLE_EZCLOCKER_SUPPORT',
            'ROLE_EZCLOCKER_SUPPORT',
            // Authority name
            'ROLE_EZCLOCKER_SUPPORT',
            // EzClockerAccountType
            'SUPPORT',
            // Role name
            'Support',
            // Context Name
            'supportContext',
            // Access Information
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'Full access to Employer Dashboard website page.',
                'Full access to Employer Schedules website page.',
                'Full access to Employer Archive website page.',
                'Restricted access to Employer Account website page.',
                '   Cannot view account payment information.',
                '   Cannot edit or modify account information.',
                '   Cannot subscribe.',
                '   Cannot change subscription plans.',
                '   Cannot cancel subscription plans.',
                'Full access to the Employee Dashboard website page.',
                'Full access to the Employee Schedule website page.',
                'Full access to the Personal Dashboard website page',
                'Full access to the Developer Account website page.',
                'Restricted access to the Administration Portal website page.',
                'Full access to the Employer mobile app.',
                'Full access to the Employee mobile app.',
                'Full access to the Personal mobile app.'
            ],
            // Internal Only
            true,
            // Legacy Account Type
            'support',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_EZCLOCKER_SUPPORT',
            'ROLE_EZCLOCKER_SUPPORT',
            ezClockerRole);

        this._ROLE_EZCLOCKER_SUPPORT = ezClockerRole;


        ezClockerRole = new EzClockerRole(
            'ROLE_ENGINEERING_DEBUG',
            'ROLE_ENGINEERING_DEBUG',
            // Authority name
            'ROLE_ENGINEERING_DEBUG',
            // EzClockerAccountType
            'ENGINEERING',
            // Role name
            'Engineering Debug',
            // Context Name
            'engineeringDebugContext',
            // Access Information
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website.',
                'Full access to Employer Dashboard website page.',
                'Full access to Employer Schedules website page.',
                'Full access to Employer Archive website page.',
                'Restricted access to Employer Account website page.',
                '   Cannot view account payment information.',
                '   Cannot edit or modify account information.',
                '   Cannot subscribe.',
                '   Cannot change subscription plans.',
                '   Cannot cancel subscription plans.',
                'Full access to the Employee Dashboard website page.',
                'Full access to the Employee Schedule website page.',
                'Full access to the Personal Dashboard website page',
                'Full access to the Developer Account website page.',
                'Restricted access to the Administration Portal website page.',
                'Full access to the Employer mobile app.',
                'Full access to the Employee mobile app.',
                'Full access to the Personal mobile app.'
            ],
            // Internal Only
            true,
            // Legacy Account Type
            'support',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_ENGINEERING_DEBUG',
            'ROLE_ENGINEERING_DEBUG',
            ezClockerRole);

        this._ROLE_ENGINEERING_DEBUG = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            'ROLE_DEVELOPER',
            'ROLE_DEVELOPER',
            'ROLE_DEVELOPER',
            // EzClockerAccountType
            'DEVELOPER',
            'Developer',
            // Context Name
            'developerContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Full access to ezClocker\'s Developer Account website page.',
                'Restricted access to ezClocker\'s Developer Account API.',
                '   Access requires approval from ezClocker Developer Team.',
                '   Access requires a valid developer authentication token.',
                'Restricted access to ezClocker\'s Employer API.',
                '   Access requires employer approval of developer account.',
                '   Access requires a valid ezClocker approved developer account.',
                '   Access requires authentication of an existing employer.',
                '   Access requires a valid employer authentication token.',
                'Restricted access to ezClocker\'s Employee API.',
                '   Access requires employer approval of developer account.',
                '   Access requires a valid ezClocker approved developer account.',
                '   Access requires authentication of an exiting employee.',
                '   Access requires a valid employee authentication token.',
                'No access to the ezClocker Administration Portal website.'
            ],
            false,
            'developer',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_DEVELOPER',
            'ROLE_DEVELOPER',
            ezClockerRole);

        this._ROLE_DEVELOPER = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            'ROLE_EMPLOYER',
            'ROLE_EMPLOYER',
            'ROLE_EMPLOYER',
            // EzClockerAccountType
            'EMPLOYER',
            'Employer',
            // Context Name
            'employerContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Full access to ezClocker\'s Employer API.',
                'Full access to ezClocker\'s Employee API.',
                'No access to the ezClocker Administration Portal website.'
            ],
            false,
            'employer',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_EMPLOYER',
            'ROLE_EMPLOYER',
            ezClockerRole);

        this._ROLE_EMPLOYER = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            'ROLE_PAYROLL_MANAGER',
            'ROLE_PAYROLL_MANAGER',
            'ROLE_PAYROLL_MANAGER',
            // EzClockerAccountType
            'PAYROLL_MANAGER',
            'Payroll Manager',
            // Context Name
            'payrollManagerContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Full access to the Employer Dashboard website page.',
                'Full access to the Employer\'s Schedules website page.',
                'Full access to the Employer\'s Employee Archive website page.',
                'No access to the Employer\'s Account website page.',
                'Full access to the ezClocker Employer mobile app.',
                'Full access to the ezClocker Employee mobile app.',
                'No access to the ezClocker Developer Account website page.',
                'No access to the ezClocker Administration Portal website',
            ],
            false,
            'payroll_manager',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_PAYROLL_MANAGER',
            'ROLE_PAYROLL_MANAGER',
            ezClockerRole);

        this._ROLE_PAYROLL_MANAGER = ezClockerRole;

        ezClockerRole = new EzClockerRole(
            'ROLE_MANAGER',
            'ROLE_MANAGER',
            'ROLE_MANAGER',
            // EzClockerAccountType
            'MANAGER',
            'Manager',
            // Context Name
            'managerContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Restricted access to the Employer Dashboard website page.',
                '   Cannot see another employe\'s pay rate.',
                '   Cannot export time sheets.',
                'Full access to the Employer\'s Schedules website page.',
                'Full access to the Employer\'s Employee Archive website page.',
                'No access to the Employer\'s Account website page.',
                'Full access to the ezClocker Employer mobile app',
                'Full access to the ezClocker Employee mobile app (for themselves)',
                'No access to the ezClocker Developer Account website page.',
                'No access to the ezClocker Administration Portal website.',
            ],
            false,
            'manager',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_MANAGER',
            'ROLE_MANAGER',
            ezClockerRole);

        this._ROLE_MANAGER = ezClockerRole;


        ezClockerRole = new EzClockerRole(
            'ROLE_EMPLOYEE',
            'ROLE_EMPLOYEE',
            'ROLE_EMPLOYEE',
            // EzClockerAccountType
            'EMPLOYEE',
            'Employee',
            // Context Name
            'employeeContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Full access to the ezClocker Employee Dashboard website page.',
                'Full access to the ezClocker Employee Schedule website page.',
                'Full access to the ezClocker Employee mobile app.',
                'No access to the Employer\'s Dashboard website page.',
                'No access to the Employer\'s Schedules website page.',
                'No access to the Employer\'s Archive Employee website page.',
                'No access to the Employer\'s Account website page.',
                'No access to the ezClocker Developer Account website page.',
                'No access to the ezClocker Administration Portal website.'
            ],
            false,
            'employee',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_EMPLOYEE',
            'ROLE_EMPLOYEE',
            ezClockerRole);

        this._ROLE_EMPLOYEE = ezClockerRole;


        ezClockerRole = new EzClockerRole(
            'ROLE_PERSONAL',
            'ROLE_PERSONAL',
            'ROLE_PERSONAL',
            // EzClockerAccountType
            'PERSONAL',
            'Personal',
            // Context Name
            'personalContext',
            [
                'Read access to ezClocker unsecured public website pages.',
                'Read access to the ezClocker Blog Website',
                'Full access to the ezClocker Personal Dashboard website page.',
                'Full access to the ezClocker Personal mobile app.',
                'No access to the ezClocker Employer Dashboard website page.',
                'No access to the ezClocker Employer Schedules website page.',
                'No access to the ezClocker Employee Archive website page.',
                'No access to the ezClocker Employer Account website page.',
                'No access to the ezClocker Developer Account website page.',
                'No access to the ezClocker Administration Portal website.',
                'No access to the ezClocker Employer mobile app.'
            ],
            false,
            'personal',
            // Enabled permissions
            EzArray.EMPTY,
            // Disabled permissions
            EzArray.EMPTY)
        this.#ezUserRoleSingleton.ezBuildEnumProp(
            'ROLE_PERSONAL',
            'ROLE_PERSONAL',
            ezClockerRole);

        this._ROLE_PERSONAL = ezClockerRole;
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Legacy Initialization and registration with ezApi
     * The legacy registration with ezApi will get removed in a future release!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get ezApiName() {
        return 'EzUserRole';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzUserRole_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with globalThis.ezApi.
     * @type {EzUserRole}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static #ezInstance = globalThis?.ezApi?.ezclocker?.[EzUserRole.ezApiName]
        ? globalThis.ezApi.ezclocker[EzUserRole.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzUserRole}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get ezInstance() {
        return EzUserRole.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with globalThis.ezApi.
     * @param {EzUserRole} instance
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static set ezInstance(instance) {
        if (null != EzUserRole.#ezInstance) {
            globalThis?.console?.warn('EzUserRole\'s singleton instance is already reigstered with globalThis.ezApi.');

            return;
        }

        EzUserRole.#ezInstance = instance;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static #ezApiRegistrationState = globalThis?.ezApi?.ezclocker?.[EzUserRole.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get ezApiRegistrationState() {
        return EzUserRole.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzUserRole.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets true when all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzUserRole.ezApiRegistrationState && globalThis?.ezApi?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static get #ezIsRegistered() {
        return null != EzUserRole.ezInstance &&
            EzRegistrationState.REGISTERED === EzUserRole.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     * @deprecated
     * Stop all use. Access via static class EzUserRole static methods and properties instead.
     * Will get removed in a future release.
     */
    static #ezRegistrator() {
        if (EzUserRole.#ezCanRegister && !EzUserRole.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterEnumeration(EzUserRole);

            globalThis.EzUserRole = EzUserRole;
        }
    }

    /**
     * Static Initialization of EzExportAction
     */
    static {
        if (null == this.#ezUserRoleSingleton) {
            this.#ezUserRoleSingleton = new EzUserRole();

            this.#ezBuildEnumeration();

            globalThis.EzUserRole = this;
        }

        // TODO: Remove the below code once the legacy registration support is removed.
        if (!EzUserRole.#ezIsRegistered) {
            EzUserRole.ezApiRegistrationState = EzRegistrationState.PENDING;

            EzClass.ezWaitReady(
                EzClass.ezOnEzApiReadyEventName,
                EzUserRole.#ezRegistrator);
        }
    }

    /*
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * End of Initialziation Section: !! DO NOT PLACE ANY CODE BEYOND THIS SECTION !!
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     */
}


